/**
 * Runtime discovery of feature flags across the providers exposed in
 * the dev tools panel: PostHog and Flagsmith (via OpenFeature).
 *
 * No central registry: we ask each SDK what flags it knows about *right
 * now* for the current user. SDKs do not expose human descriptions, so
 * descriptions are looked up separately in `descriptions.ts`.
 *
 * For Flagsmith we also keep an "observed keys" Set populated by the
 * OpenFeature override provider so flags evaluated before the cache hits
 * still show up in the panel.
 */
import type { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';
import { OpenFeature } from '@openfeature/react-sdk';
import posthog from 'posthog-js';

import {
  type FlagOverrideValue,
  type FlagProvider,
  getOverride,
} from '@/features/flags-dev/overrides';
import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { logger } from '@/utils/logger/logger';
import { getFlagDescription, getFlagVariants, getKnownFlagKeys } from './descriptions';

/**
 * Normalised raw value as the provider would return it absent any
 * override. We render this in the panel as the "real" value next to the
 * (possibly overridden) effective value.
 */
export type FlagRawValue = boolean | string | number | undefined;

export interface DevFlag {
  provider: FlagProvider;
  key: string;
  /** The provider's currently resolved value (no override applied). */
  rawValue: FlagRawValue;
  /** Override currently in effect, if any. */
  override: FlagOverrideValue | undefined;
  /** Optional human description from `descriptions.ts`. */
  description?: string;
  /** Known string variants that dev tools can force for this flag. */
  variants?: readonly string[];
}

/**
 * Keys we have seen flow through OpenFeature evaluations but that
 * Flagsmith's `getAllFlags()` may not return (e.g. flag was queried
 * before the cache hydrated, or it lives only in a fallback). The
 * override provider populates this; discovery merges it with `getAllFlags`.
 */
const observedFlagsmithKeys = new Set<string>();

export const recordFlagsmithKey = (flagKey: string): void => {
  observedFlagsmithKeys.add(flagKey);
};

/**
 * Keys the app has queried via `posthog.isFeatureEnabled` /
 * `posthog.getFeatureFlag` since boot. PostHog's `getFlags()` only
 * returns flags it has evaluated for the current user, so flags that
 * exist in the project but the user is excluded from never appear in
 * the helper. The call tracker (see `posthog-call-tracker.ts`)
 * populates this so we can show an override row for every flag the app
 * actually reads, even when the user isn't in the cohort.
 */
const observedPostHogKeys = new Set<string>();

export const recordPostHogKey = (flagKey: string): void => {
  if (!flagKey) return;
  observedPostHogKeys.add(flagKey);
};

const listPostHogFlags = (): DevFlag[] => {
  if (typeof window === 'undefined') return [];
  const keys = new Set<string>([...getKnownFlagKeys('posthog'), ...observedPostHogKeys]);
  let variants: Record<string, FlagRawValue> = {};

  try {
    const featureFlags = posthog.featureFlags;
    if (featureFlags && typeof featureFlags.getFlags === 'function') {
      for (const key of featureFlags.getFlags()) {
        keys.add(key);
      }
    }
    // Read raw flag values directly from persistence, NOT from
    // `featureFlags.getFlagVariants()`. The latter merges the override
    // map (`$override_feature_flags`) on top of the server-evaluated
    // values, so once a dev-tools override is active the "Provider value"
    // we display below would echo the override instead of the real
    // backend value — exactly the diagnostic the panel exists to show.
    // Persistence key is `$enabled_feature_flags` (prefixed); the
    // unprefixed `feature_flags` belongs to a different enum (bootstrap
    // config) and is NOT a key on `persistence.props`.
    const persistedProps = (
      posthog as unknown as { persistence?: { props?: Record<string, unknown> } }
    ).persistence?.props;
    const rawVariants = persistedProps?.['$enabled_feature_flags'] as
      | Record<string, FlagRawValue>
      | undefined;
    if (rawVariants) variants = rawVariants;
  } catch (err) {
    logger.warn('[flags-dev] PostHog flag discovery failed', { error: String(err) });
  }

  return Array.from(keys).map<DevFlag>((key) => ({
    provider: 'posthog',
    key,
    rawValue: variants[key],
    override: getOverride('posthog', key),
    description: getFlagDescription('posthog', key),
    variants: getFlagVariants('posthog', key),
  }));
};

const listFlagsmithFlags = (): DevFlag[] => {
  if (typeof window === 'undefined') return [];
  const keys = new Set<string>([
    ...getKnownFlagKeys('flagsmith'),
    ...observedFlagsmithKeys,
  ]);
  let flags: Record<string, { enabled: boolean; value?: unknown }> | undefined;

  try {
    const provider = OpenFeature.getProvider(
      FEATURE_FLAG_DOMAIN
    ) as Partial<FlagsmithClientProvider> & { flagsmithClient?: unknown };
    const flagsmithClient = provider.flagsmithClient as
      | { getAllFlags?: () => Record<string, { enabled: boolean; value?: unknown }> }
      | undefined;
    if (flagsmithClient && typeof flagsmithClient.getAllFlags === 'function') {
      flags = flagsmithClient.getAllFlags();
      for (const key of Object.keys(flags ?? {})) {
        keys.add(key);
      }
    }
  } catch (err) {
    logger.warn('[flags-dev] Flagsmith flag discovery failed', { error: String(err) });
  }

  return Array.from(keys)
    .sort()
    .map<DevFlag>((key) => {
      const flag = flags?.[key];
      const rawValue: FlagRawValue = flag
        ? ((flag.value as FlagRawValue) ?? flag.enabled)
        : undefined;
      return {
        provider: 'flagsmith',
        key,
        rawValue,
        override: getOverride('flagsmith', key),
        description: getFlagDescription('flagsmith', key),
        variants: getFlagVariants('flagsmith', key),
      };
    });
};

/**
 * Snapshot every known flag right now. Cheap enough to call on every
 * panel open / atom change — no caching here so the UI always reflects
 * the latest SDK state without manual invalidation.
 */
export const listAllFlags = (): DevFlag[] => {
  return [...listPostHogFlags(), ...listFlagsmithFlags()];
};
